import type { FoodOption, Macros, NutritionInfo } from "@/data/meal-plan/types.ts";

/**
 * Food options for "Romina Nutri 2" (1261 kcal, recomposición corporal).
 *
 * Naming: option names reuse the existing names in `options.ts` /
 * `plan-romina/options.ts` wherever the food is the same, because the Compras
 * screen links store products by option name and aggregates `grams × count`
 * per name.
 *
 * Weights, as interpreted from the nutritionist's sheet:
 * - Arroz / fideos / menestras / papa: COOKED weight, with cooked nutrition —
 *   same convention as the existing "Arroz blanco 50 g", "Fideos blancos 48 g"
 *   and "Lentejas 80 g cocido" options.
 * - Filetes (pollo, pescado, cerdo, res, pavita) and carne molida: RAW weight
 *   (`grams`), like every existing meat option; `cookedGrams` uses the same
 *   yield as the existing options (pollo/pavita ×0.75, the rest ×0.714).
 * - Atún "1 lata": the standard Peruvian 140 g can drains to 100 g (label of
 *   Bell's, Florida, Primor at Plaza Vea), so `grams` = 100 g drained.
 * - "Menestras" is a choice of legume (lentejas, frejol, garbanzo, arvejita
 *   partida), reusing the existing legume option names; the old plan renamed
 *   its "Menestras" option to "Lentejas", which only covers one of them.
 *
 * Ids are descriptive slugs: stable, and unique within each group.
 */

const round1 = (x: number) => Math.round(x * 10) / 10;

/** Nutrition for a weighed food: perPortion = per100g × grams / 100. */
function weighed(sourceName: string, per100g: Macros, grams: number): NutritionInfo {
  const f = grams / 100;
  return {
    sourceName,
    per100g,
    perPortion: {
      calories: round1(per100g.calories * f),
      protein: round1(per100g.protein * f),
      carbs: round1(per100g.carbs * f),
      fat: round1(per100g.fat * f),
    },
  };
}

// ── per-100 g reference values ──
// Reused from existing options where the food already exists.

const OATS = { calories: 389, protein: 16.9, carbs: 66.3, fat: 6.9 }; // existing
const RICE_COOKED = { calories: 130, protein: 2.7, carbs: 28.2, fat: 0.3 }; // existing
const PASTA_COOKED = { calories: 158, protein: 5.8, carbs: 30.9, fat: 0.9 }; // existing
const POTATO_BOILED = { calories: 86, protein: 1.7, carbs: 20.0, fat: 0.1 }; // existing
const LENTILS_COOKED = { calories: 116, protein: 9.0, carbs: 20.1, fat: 0.4 }; // existing
const PINTO_COOKED = { calories: 143, protein: 9.0, carbs: 26.2, fat: 0.65 }; // existing
const CHICKPEAS_COOKED = { calories: 164, protein: 8.86, carbs: 27.4, fat: 2.59 }; // existing
const SPLIT_PEAS_COOKED = { calories: 118, protein: 8.34, carbs: 21.1, fat: 0.4 }; // existing
const CHICKEN_RAW = { calories: 120, protein: 22.5, carbs: 0, fat: 2.6 }; // existing
const TILAPIA_RAW = { calories: 96, protein: 20.1, carbs: 0, fat: 1.7 }; // existing
const BEEF_LEAN_RAW = { calories: 143, protein: 21.4, carbs: 0, fat: 5.9 }; // existing
const GROUND_BEEF_RAW = { calories: 176, protein: 20.0, carbs: 0, fat: 10.0 }; // existing
const TUNA_WATER = { calories: 116, protein: 25.5, carbs: 0, fat: 0.8 }; // existing
const AVOCADO = { calories: 160, protein: 2.0, carbs: 8.5, fat: 14.7 }; // existing
const BLUEBERRIES = { calories: 57, protein: 0.7, carbs: 14.5, fat: 0.3 }; // existing
const STRAWBERRIES = { calories: 32, protein: 0.7, carbs: 7.7, fat: 0.3 }; // existing
const TANGERINE = { calories: 53, protein: 0.8, carbs: 13.3, fat: 0.3 }; // existing

// New foods — USDA FoodData Central (SR Legacy) raw values.
const PORK_TENDERLOIN_RAW = { calories: 120, protein: 20.95, carbs: 0, fat: 3.39 };
const TURKEY_BREAST_RAW = { calories: 114, protein: 23.66, carbs: 0.14, fat: 1.48 };
const CHIA = { calories: 486, protein: 16.5, carbs: 42.1, fat: 30.7 };
const BANANA = { calories: 89, protein: 1.09, carbs: 22.84, fat: 0.33 };
const PINEAPPLE = { calories: 50, protein: 0.54, carbs: 13.12, fat: 0.12 };
const WATERMELON = { calories: 30, protein: 0.61, carbs: 7.55, fat: 0.15 };
const PAPAYA = { calories: 43, protein: 0.47, carbs: 10.82, fat: 0.26 };
const KIWI = { calories: 61, protein: 1.14, carbs: 14.66, fat: 0.52 };
const APPLE = { calories: 52, protein: 0.26, carbs: 13.81, fat: 0.17 };
const PEAR = { calories: 57, protein: 0.36, carbs: 15.23, fat: 0.14 };
const GOLDENBERRY = { calories: 53, protein: 1.9, carbs: 11.2, fat: 0.7 }; // USDA "Groundcherries (cape-gooseberries or poha), raw"
const PRICKLY_PEAR = { calories: 41, protein: 0.73, carbs: 9.57, fat: 0.51 };

// New foods — Peruvian product labels (Plaza Vea / brand sites, Oct 2026).
// Fat not printed on some labels: derived as (kcal − 4·P − 4·C) / 9.
/** Yogurt Quark Vakimu Natural (pote 500 g): 48.9 kcal, 11.6 P, 0.4 C per 100 g. */
const QUARK_VAKIMU = { calories: 48.9, protein: 11.6, carbs: 0.4, fat: 0.1 };
/** Yogurt Skyr Plusa natural (pote 500 g): 55 kcal, 11 P, 2.7 C per 100 g. */
const SKYR_PLUSA = { calories: 55, protein: 11, carbs: 2.7, fat: 0 };
/** Queso Fresco Light Bonlé: 47 kcal, 5.5 P, 0.7 C per 30 g. */
const QUESO_LIGHT_BONLE = { calories: 156.7, protein: 18.3, carbs: 2.3, fat: 8.2 };
/** Yogurt bebible Vakimu +Pro vainilla, botella 350 g: 46 kcal, 6.1 P, 4.3 C per 100 g. */
const VAKIMU_PRO_DRINK = { calories: 46, protein: 6.1, carbs: 4.3, fat: 0.5 };
/** Generic whey protein isolate: 1 scoop 30 g ≈ 110 kcal, 25 P, 1 C, 0.5 F. */
const WHEY_ISOLATE = { calories: 366.7, protein: 83.3, carbs: 3.3, fat: 1.7 };

// ── Carbs ──

export const ROMINA2_OATS: FoodOption = {
  id: "avena", name: "Avena cruda (cocinar)", portion: "20 g", grams: 20,
  nutrition: weighed("Avena en hojuelas cruda (USDA)", OATS, 20),
};

/** Same values as the existing "Galletas Salmas" option (18 g pack, 63 kcal). */
export const ROMINA2_SALMAS: FoodOption = {
  id: "salmas", name: "Galletas Salmas", portion: "1 paquete (3 und)", nutrition: {
    sourceName: "Salmas Sanissimo",
    per100g: { calories: 350, protein: 8.3, carbs: 70, fat: 3.3 },
    perPortion: { calories: 63.0, protein: 1.5, carbs: 12.6, fat: 0.6 },
  },
};

/** "arroz o fideos" — cooked weight. */
function riceOrPasta(grams: number): FoodOption[] {
  return [
    { id: "arroz", name: "Arroz blanco", portion: `${grams} g cocido`, grams,
      nutrition: weighed("Arroz blanco cocido grano largo (USDA #168878)", RICE_COOKED, grams) },
    { id: "fideos", name: "Fideos blancos", portion: `${grams} g cocido`, grams,
      nutrition: weighed("Pasta cocida enriquecida (USDA #169737)", PASTA_COOKED, grams) },
  ];
}
export const ROMINA2_LUNCH_RICE_OR_PASTA = riceOrPasta(100);
export const ROMINA2_DINNER_RICE_OR_PASTA = riceOrPasta(50);

function potato(grams: number): FoodOption {
  return { id: "papa", name: "Papa", portion: `${grams} g`, grams,
    nutrition: weighed("Papa hervida sin piel (USDA #170440)", POTATO_BOILED, grams) };
}
export const ROMINA2_LUNCH_POTATO = potato(160);
export const ROMINA2_DINNER_POTATO = potato(80);

/** "menestras" — choice of legume, cooked weight. */
function legumes(grams: number): FoodOption[] {
  const portion = `${grams} g cocido`;
  return [
    { id: "lentejas", name: "Lentejas", portion, grams,
      nutrition: weighed("Lentejas cocidas hervidas (USDA #172421)", LENTILS_COOKED, grams) },
    { id: "frejol", name: "Frejol", portion, grams,
      nutrition: weighed("Frijoles pintos cocidos hervidos (USDA #175194)", PINTO_COOKED, grams) },
    { id: "garbanzo", name: "Garbanzo", portion, grams,
      nutrition: weighed("Garbanzos cocidos hervidos (USDA #173757)", CHICKPEAS_COOKED, grams) },
    { id: "arvejita", name: "Arvejita partida", portion, grams,
      nutrition: weighed("Arvejas partidas cocidas hervidas (USDA #175216)", SPLIT_PEAS_COOKED, grams) },
  ];
}
export const ROMINA2_LUNCH_LEGUMES = legumes(150);
export const ROMINA2_DINNER_LEGUMES = legumes(75);

// ── Proteins ──

/** Same values as the existing "Huevo entero" option (50 g egg). */
export const ROMINA2_EGG: FoodOption = {
  id: "huevo", name: "Huevo entero", portion: "1 und", nutrition: {
    sourceName: "Huevo de gallina entero crudo (USDA)",
    per100g: { calories: 155, protein: 12.6, carbs: 1.1, fat: 11.3 },
    perPortion: { calories: 77.5, protein: 6.3, carbs: 0.6, fat: 5.7 },
  },
};

/** "100 g yogurt QUARK o SKYR". */
export const ROMINA2_QUARK_OR_SKYR: FoodOption[] = [
  { id: "quark", name: "Yogurt Quark", portion: "100 g", grams: 100,
    nutrition: weighed("Yogurt Quark Vakimu Natural", QUARK_VAKIMU, 100) },
  { id: "skyr", name: "Yogurt Skyr", portion: "100 g", grams: 100,
    nutrition: weighed("Yogurt Skyr Plusa Natural", SKYR_PLUSA, 100) },
];

export const ROMINA2_LIGHT_CHEESE: FoodOption = {
  id: "queso-light", name: "Queso fresco light", portion: "80 g", grams: 80,
  nutrition: weighed("Queso Fresco Light Bonlé", QUESO_LIGHT_BONLE, 80),
};

function whey(grams: number, portion: string): FoodOption {
  return { id: "proteina", name: "Proteína whey isolate", portion, grams,
    nutrition: weighed("Proteína whey isolate (genérico, 1 scoop = 30 g)", WHEY_ISOLATE, grams) };
}
export const ROMINA2_WHEY_HALF_SCOOP = whey(15, "½ scoop (15 g)");
export const ROMINA2_WHEY_SCOOP = whey(30, "1 scoop (30 g)");

/** 1 lata de 140 g = 100 g escurrido. */
export const ROMINA2_TUNA_CAN: FoodOption = {
  id: "atun", name: "Atún en conserva", portion: "1 lata (100 g escurrido)", grams: 100,
  nutrition: weighed("Atún enlatado en agua (USDA)", TUNA_WATER, 100),
};

const FILLET_GRAMS = 150;
const fillet = {
  pollo: { id: "pollo", name: "Pollo", portion: "150 g crudo", grams: FILLET_GRAMS, cookedGrams: 113,
    nutrition: weighed("Pechuga de pollo cruda sin piel (USDA)", CHICKEN_RAW, FILLET_GRAMS) },
  pescado: { id: "pescado", name: "Pescado", portion: "150 g crudo", grams: FILLET_GRAMS, cookedGrams: 107,
    nutrition: weighed("Tilapia cruda (USDA)", TILAPIA_RAW, FILLET_GRAMS) },
  cerdo: { id: "cerdo", name: "Cerdo", portion: "150 g crudo", grams: FILLET_GRAMS, cookedGrams: 107,
    nutrition: weighed("Lomo de cerdo crudo, magro (USDA)", PORK_TENDERLOIN_RAW, FILLET_GRAMS) },
  res: { id: "res", name: "Carne de res", portion: "150 g crudo", grams: FILLET_GRAMS, cookedGrams: 107,
    nutrition: weighed("Carne de res magra cruda (USDA)", BEEF_LEAN_RAW, FILLET_GRAMS) },
  pavita: { id: "pavita", name: "Pavita", portion: "150 g crudo", grams: FILLET_GRAMS, cookedGrams: 113,
    nutrition: weighed("Pechuga de pavo cruda sin piel (USDA)", TURKEY_BREAST_RAW, FILLET_GRAMS) },
} satisfies Record<string, FoodOption>;

/** "150 g filete de pollo, pescado, cerdo o res" (Opción 1). */
export const ROMINA2_FILLETS_WITH_PORK: FoodOption[] = [fillet.pollo, fillet.pescado, fillet.cerdo, fillet.res];
/** "150 g filete de pollo, pavita, pescado o res" (Opción 3). */
export const ROMINA2_FILLETS_WITH_TURKEY: FoodOption[] = [fillet.pollo, fillet.pavita, fillet.pescado, fillet.res];

/** "150 g carne molida premium" — same 90/10 values as the existing "Carne molida". */
export const ROMINA2_GROUND_BEEF: FoodOption = {
  id: "carne-molida", name: "Carne molida", portion: "150 g crudo", grams: FILLET_GRAMS, cookedGrams: 107,
  nutrition: weighed("Carne molida 90/10 cruda (USDA #174036)", GROUND_BEEF_RAW, FILLET_GRAMS),
};

/** Pózima, botella 330 ml — label per 100 ml: 62 kcal, 6.5 P, 5.7 C, 1.5 F. */
export const ROMINA2_POZIMA: FoodOption = {
  id: "pozima", name: "Pozima", portion: "1 botella (330 ml)", nutrition: {
    sourceName: "Pózima bebida proteica (clara de huevo), botella 330 ml",
    per100g: { calories: 62, protein: 6.5, carbs: 5.7, fat: 1.5 },
    perPortion: { calories: 204.6, protein: 20, carbs: 18.9, fat: 4.8 },
  },
};

/**
 * "1 botella de VAKIMU +PRO (350g)": the drinkable yogurt bottle, a different
 * product (and different nutrition) from the existing "Vakimu Pro" 80 g option
 * (spoonable pote), so it gets its own name for Compras. Bought by the bottle,
 * so Compras counts bottles (no `grams`); nutrition is still per 350 g bottle.
 */
export const ROMINA2_VAKIMU_PRO_DRINK: FoodOption = {
  id: "vakimu-pro", name: "Vakimu +Pro bebible", portion: "1 botella (350 g)",
  nutrition: weighed("Yogurt bebible Vakimu +Pro vainilla, botella 350 g", VAKIMU_PRO_DRINK, 350),
};

/**
 * Gloria Pro Day chocolate, botella 320 ml. Retail label: 184 kcal per bottle;
 * per 100 ml 6.3 P, 6.5 C (Gloria: 20.2 g protein per bottle). Fat derived.
 */
export const ROMINA2_PRO_DAY: FoodOption = {
  id: "pro-day", name: "Pro Day Gloria", portion: "1 botella (320 ml)", nutrition: {
    sourceName: "Bebida láctea Gloria Pro Day chocolate, botella 320 ml",
    per100g: { calories: 57.5, protein: 6.3, carbs: 6.5, fat: 0.7 },
    perPortion: { calories: 184, protein: 20.2, carbs: 20.8, fat: 2.2 },
  },
};

// ── Fats ──

/** 1 cda ≈ 12 g. */
export const ROMINA2_CHIA: FoodOption = {
  id: "chia", name: "Chía", portion: "1 cda (12 g)", grams: 12,
  nutrition: weighed("Semillas de chía secas (USDA)", CHIA, 12),
};

function avocado(grams: number): FoodOption {
  return { id: "palta", name: "Palta", portion: `${grams} g`, grams,
    nutrition: weighed("Aguacate/Palta (USDA)", AVOCADO, grams) };
}
export const ROMINA2_AVOCADO_40 = avocado(40);
export const ROMINA2_AVOCADO_80 = avocado(80);

/**
 * Same name and unit as the existing "Aceite de oliva 1 cdta" (no grams, so
 * Compras counts cdtas). The sheet states 1 cdta = 5 g ("2 cdtas (10g)"), so
 * the portion macros use 5 g rather than the existing option's 4.5 g.
 */
export const ROMINA2_OLIVE_OIL: FoodOption = {
  id: "aceite", name: "Aceite de oliva", portion: "1 cdta", nutrition: {
    sourceName: "Aceite de oliva extra virgen (USDA)",
    per100g: { calories: 884, protein: 0, carbs: 0, fat: 100 },
    perPortion: { calories: 44.2, protein: 0, carbs: 0, fat: 5 },
  },
};

// ── Fruits ──

function fruit(id: string, name: string, grams: number, sourceName: string, per100g: Macros): FoodOption {
  return { id, name, portion: `${grams} g`, grams, nutrition: weighed(sourceName, per100g, grams) };
}

export const ROMINA2_BANANA = fruit("platano", "Plátano", 80, "Plátano de seda crudo (USDA)", BANANA);
export const ROMINA2_PINEAPPLE = fruit("pina", "Piña", 180, "Piña cruda (USDA)", PINEAPPLE);
export const ROMINA2_WATERMELON = fruit("sandia", "Sandía", 270, "Sandía cruda (USDA)", WATERMELON);
export const ROMINA2_BLUEBERRIES = fruit("arandanos", "Arándanos", 105, "Arándanos frescos (USDA)", BLUEBERRIES);

/** "1 porc fruta" list from the sheet (cell C12), in the sheet's order. */
export const ROMINA2_FRUIT_PORTION: FoodOption[] = [
  ROMINA2_BLUEBERRIES,
  fruit("fresas", "Fresa", 220, "Fresas frescas (USDA)", STRAWBERRIES),
  fruit("papaya", "Papaya", 200, "Papaya cruda (USDA)", PAPAYA),
  ROMINA2_PINEAPPLE,
  fruit("kiwi", "Kiwi", 105, "Kiwi verde crudo (USDA)", KIWI),
  fruit("manzana", "Manzana", 140, "Manzana cruda con cáscara (USDA)", APPLE),
  fruit("mandarina", "Mandarinas", 220, "Mandarina fresca (USDA)", TANGERINE),
  fruit("pera", "Pera", 140, "Pera cruda (USDA)", PEAR),
  fruit("aguaymanto", "Aguaymanto", 135, "Aguaymanto crudo (USDA, groundcherries)", GOLDENBERRY),
  fruit("tuna", "Tuna", 180, "Tuna (higo chumbo) cruda (USDA, prickly pears)", PRICKLY_PEAR),
  ROMINA2_BANANA,
];
